import React, { FC, useRef } from 'react';
import {
  flexRender,
  getCoreRowModel,
  useReactTable,
  getSortedRowModel,
  ColumnDef,
  SortingState,
  OnChangeFn,
} from '@tanstack/react-table';
import { twMerge } from 'tailwind-merge';
import { useVirtualizer } from '@tanstack/react-virtual';
import { LiaSortDownSolid, LiaSortSolid, LiaSortUpSolid } from 'react-icons/lia';

interface GenericTableProps<T> {
  data: T[];
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  columns: ColumnDef<T, any>[];
  sorting?: SortingState;
  onSortingChange?: OnChangeFn<SortingState>;
  enableSorting?: boolean;
  rowActions?: (row: T) => React.ReactNode;
  onRowClick?: (row: T) => void;
  onScrollBottom?: () => void;
  containerRef?: React.RefObject<HTMLDivElement>;
  isFetching?: boolean;
  gridColClass?: string;
  gridColBorderClass?: string;
  hover?: boolean;
  maxWidth?: string;
  minWidth?: string;
  widthClass?: string;
}
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const GenericTable: FC<GenericTableProps<any>> = ({
  data,
  columns,
  sorting,
  onSortingChange,
  enableSorting = true,
  rowActions,
  onRowClick,
  onScrollBottom,
  containerRef,
  isFetching,
  gridColClass,
  gridColBorderClass,
  hover = true,
  maxWidth,
  minWidth,
  widthClass = 'w-full',
}) => {
  const tableContainerRef = containerRef || useRef<HTMLDivElement>(null);

  const tableInstance = useReactTable({
    columns,
    data,
    state: {
      sorting,
    },
    onSortingChange,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    manualSorting: !enableSorting,
  });

  const { rows } = tableInstance.getRowModel();

  const rowVirtualizer = useVirtualizer({
    count: rows.length,
    estimateSize: () => 95,
    getScrollElement: () => tableContainerRef.current,
    measureElement:
      typeof window !== 'undefined' && navigator.userAgent.indexOf('Firefox') === -1
        ? (element) => element?.getBoundingClientRect().height
        : undefined,
    overscan: 5,
  });

  const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
    if (onScrollBottom && !isFetching) {
      const { scrollHeight, scrollTop, clientHeight } = e.currentTarget;
      if (scrollHeight - scrollTop - clientHeight < 500) {
        onScrollBottom();
      }
    }
  };

  return (
    <div
      className="scrollbar-phenom relative h-full max-h-[80vh] w-full overflow-auto scrollbar-track-transparent scrollbar-thumb-ph-primary-violet"
      ref={tableContainerRef}
      onScroll={handleScroll}
    >
      <table className={twMerge('table-fixed', maxWidth, minWidth, widthClass)}>
        <thead className="sticky top-0 z-10">
          {tableInstance.getHeaderGroups().map((headerGroup) => (
            <tr className={twMerge('relative grid h-10 text-left', gridColClass, widthClass)} key={headerGroup.id}>
              {headerGroup.headers.map((header) => {
                const canHeaderSort = header.column.columnDef.enableSorting ?? false;

                return (
                  <th
                    key={header.id}
                    className={twMerge(
                      hover && 'hover:cursor-pointer hover:text-ph-gray-silver',
                      'backdrop-blur',
                      'flex items-center px-3 font-manrope text-sm font-bold text-ph-gray-evening first:rounded-l-xl first:pl-5 last:rounded-r-xl',
                      gridColBorderClass,
                    )}
                  >
                    <div
                      className="mr-4 flex w-full items-center justify-start gap-1 truncate"
                      onClick={canHeaderSort ? header.column.getToggleSortingHandler() : undefined}
                    >
                      {flexRender(header.column.columnDef.header, header.getContext())}
                      <span>
                        {
                          {
                            asc: <LiaSortUpSolid />,
                            desc: <LiaSortDownSolid />,
                          }[(header.column.getIsSorted() as string) ?? null]
                        }
                        {canHeaderSort && !header.column.getIsSorted() && <LiaSortSolid />}
                      </span>
                    </div>
                  </th>
                );
              })}
            </tr>
          ))}
        </thead>
        <tbody
          className="relative mt-2 grid gap-y-1"
          style={{
            height: `${rowVirtualizer.getTotalSize() + 20}px`,
          }}
        >
          {rowVirtualizer.getVirtualItems().map((virtualRow) => {
            const row = rows[virtualRow.index];
            return (
              <tr
                data-index={virtualRow.index}
                ref={(node) => rowVirtualizer.measureElement(node)}
                key={row.id}
                style={{
                  transform: `translateY(${virtualRow.start}px)`,
                }}
                className={twMerge(
                  hover && 'hover:bg-ph-gray-baltic',
                  'absolute flex grid items-center text-ph-gray-evening',
                  gridColClass,
                  widthClass,
                )}
                onClick={() => onRowClick?.(row.original.id)}
              >
                {row.getVisibleCells().map((cell) => (
                  <td
                    key={cell.id}
                    className={twMerge(
                      hover && 'hover:cursor-pointer hover:bg-ph-gray-baltic',
                      'flex items-center justify-start truncate py-3 font-manrope text-sm font-bold first:pl-0 md:first:pl-5',
                    )}
                  >
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </td>
                ))}
                {rowActions && <td>{rowActions(row.original)}</td>}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

export default GenericTable;
